import React, { useEffect, useState } from 'react';
import { Input, Table, Button, DOMHelper, Stack, IconButton, Checkbox, Divider } from 'rsuite';
import type { SortType } from 'rsuite';
import EditIcon from '@rsuite/icons/Edit';
import { BaseErrorModel, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import FetchingSelect from '@/components/FetchingSelect';
import TablePagination from '@/components/TablePagination';
import { toastErrorMessage } from '@/utils/toasterMessage';
import AudioCell from '@/components/ImageCell';
import { StoryChapterList, StoryChapterPaginationFilterRequest } from '@/models/story-chapter.model';
import { getPagedStoryChapters } from '@/services/story-chapter.service';
import { getStoriesLookup } from '@/services/story.service';
import PageContent from '@/components/PageContent';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;
interface StoryChapterListPageProps{
  storyId?: number; 
};

const StoryChapterListPage : React.FC<StoryChapterListPageProps> = ({storyId}) => {

  const [filterReq, setFilterReq] = useState<StoryChapterPaginationFilterRequest>(new StoryChapterPaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<StoryChapterList[]>([]);
  const [loading, setLoading] = useState(true);

  const nav = useNavigate();

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: StoryChapterPaginationFilterRequest) => {
    setLoading(true);
    try {
      const finalReq = {...req, storyId: storyId ?? filterReq.storyId};
      const result: PagedResponseModel<StoryChapterList> = await getPagedStoryChapters(finalReq);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ 
          ...pre, 
          pageNumber: result.pageNumber, 
          totalRecords: result.totalRecords, 
          pageSize: result.pageSize 
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching Story Chapters:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre, 
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Button appearance="primary" onClick={() => nav(`/stories/chapters/new`, {state:{storyId}})}>
          Add Chapter
        </Button>
        <Stack spacing={6}>             
          {!storyId && <FetchingSelect
            label="Stories"
            fetchData={(text, page) => getStoriesLookup(text, page)}
            value={filterReq.storyId}
            onChange={dataKey => setFilterReq(pre => ({...pre, storyId: dataKey}))}
          />}
          <Input 
            placeholder="Keyword" 
            value={filterReq.keyword ?? ''} 
            onChange={dataKey => setFilterReq(pre => ({...pre, keyword: dataKey}))}
          />
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >
        <Column flexGrow={0.5} align="center" sortable>
          <HeaderCell>Order</HeaderCell>
          <Cell dataKey="order" />
        </Column>
        <Column flexGrow={0.5} align="center" >
          <HeaderCell>Banner</HeaderCell>
          <AudioCell dataKey="bannerUrl" />
        </Column>
        <Column flexGrow={2} align="left" sortable>
          <HeaderCell>Name</HeaderCell>
          <Cell dataKey="name"/>
        </Column>
        <Column flexGrow={1} align="center" >
          <HeaderCell>Is Intro</HeaderCell>
          <Cell style={{ padding: 5 }}>
          {(rowData: StoryChapterList) => (   
            <Checkbox checked={rowData.isIntro} readOnly></Checkbox>
          )}
          </Cell>
        </Column>        
        <Column flexGrow={0.5} align="center" sortable>
          <HeaderCell>Minimum Star</HeaderCell>
          <Cell dataKey="minimumStar" />
        </Column>
        <Column flexGrow={0.5} align="center" sortable>
          <HeaderCell>Tasks</HeaderCell>
          <Cell dataKey="tasks" />
        </Column>
        <Column flexGrow={0.5} align="center" sortable>
          <HeaderCell>Medias</HeaderCell>
          <Cell dataKey="medias" />
        </Column>
        <Column flexGrow={0.5} fixed="right" >
        <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {rowData => (         
              <IconButton onClick={() => nav(`/stories/chapters/${rowData.id}`)} icon={<EditIcon />} circle size="sm" />  
            )}
          </Cell>
        </Column>
        
      </Table>
      <Divider />
      <TablePagination 
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
        onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
      />
    </PageContent>
  );
};

export default StoryChapterListPage;
